<template>
  <div :id="componentId" class="inno-carousel-product-detail mb-6">
    <div class="container mx-auto mb-4 flex max-w-screen-xl items-center justify-between px-0">
      <VcTypography tag="h4" v-if="title">
        {{ title }}
      </VcTypography>

      <div class="w-full h-[1px] bg-neutral-300 mx-4 flex-1"></div>
      <!-- Navigation buttons-->
      <div v-if="showNavigation" class="inno-carousel-product-detail__navigation flex">
        <div class="inno-carousel-product-detail__btn inno-carousel-product-detail__btn--prev">
          <VcIcon
            :class="{ 'rotate-90': !isMobile }"
            class="relative flex justify-center items-center top-0.5"
            name="chevron-left-small"
            :size="14"
          />
        </div>

        <div class="inno-carousel-product-detail__btn inno-carousel-product-detail__btn--next ml-2">
          <VcIcon
            :class="{ 'rotate-90': !isMobile }"
            class="relative flex justify-center items-center top-0.5"
            name="chevron-right-small"
            :size="14"
          />
        </div>
      </div>
    </div>

    <div
      class="inno-carousel-product-detail__wrapper"
      :class="{
        'md:h-[80px]': slides.length === 1,
        'md:h-[160px]': slides.length === 2,
        'md:h-[240px]': slides.length >= 3,
      }"
    >
      <Swiper
        :direction="isMobile ? 'horizontal' : 'vertical'"
        :modules="modules"
        :navigation="navigationParams"
        :simulate-touch="showNavigation"
        v-bind="attrs"
        v-on="listeners"
        class="w-full h-full"
      >
        <SwiperSlide v-for="(slide, index) in slides" :key="index" class="md:!h-[70px] mb-[10px]">
          <slot name="slide" v-bind="{ slide, index }">
            {{ slide }}
          </slot>
        </SwiperSlide>
      </Swiper>
    </div>
  </div>
</template>

<script setup lang="ts">
import _ from "lodash";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/vue";
import { computed, getCurrentInstance } from "vue";
import type { NavigationOptions } from "swiper/types";
import type { PropType } from "vue";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import type { Product } from "@/core/api/graphql/types";

const props = defineProps({
  slides: {
    type: Array as PropType<Product[]>,
    default: () => [],
  },

  options: {
    type: Object as PropType<ICarouselOptions>,
    default: () => ({}),
  },

  pagination: {
    type: Boolean,
    default: false,
  },

  title: {
    type: String,
    default: "",
  },

  titleComponent: {
    type: String,
    default: "",
  },
});

const componentId = `inno-carousel-product-detail_${getCurrentInstance()!.uid}`;
const modules = [Navigation];

const listeners = computed(() => props.options.on ?? {});
const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller("xl");

const attrs = computed<Omit<ICarouselOptions, "on">>(() => {
  const options = _.clone(props.options);
  delete options.on;
  return options;
});

const showNavigation = computed(() => (isMobile.value ? true : props.slides.length > 3));

const navigationParams = computed<NavigationOptions | boolean>(() => ({
  prevEl: `#${componentId} .inno-carousel-product-detail__btn--prev`,
  nextEl: `#${componentId} .inno-carousel-product-detail__btn--next`,
  lockClass: "inno-carousel-product-detail__btn--lock",
  hiddenClass: "inno-carousel-product-detail__btn--hidden",
  disabledClass: "inno-carousel-product-detail__btn--disabled",
}));
</script>

<style lang="scss">
.inno-carousel-product-detail {
  $self: &;

  --navigation-size: 36px;
  --navigation-offset: 0px;
  --pagination-offset: -5px;

  --swiper-pagination-bullet-size: 13px;
  --swiper-pagination-bullet-inactive-color: transparent;
  --swiper-pagination-bullet-inactive-opacity: 1;

  &__btn {
    @apply flex justify-center items-center w-6 h-6 cursor-pointer border rounded-sm;

    &--prev {
      @apply left-0;
    }

    &--next {
      @apply right-0;
    }

    &--disabled {
      @apply cursor-auto pointer-events-none opacity-30;
    }

    &--lock {
      @apply hidden;
    }
  }
}
</style>
